/**
 * @fileoverview Generic inline lookup field with suggestion popover and explicit confirmation control.
 * @module shared/components/LookupConfirmField
 */

import { useQuery, type QueryKey, type UseQueryOptions } from '@tanstack/react-query'
import { Check, Loader2, Plus, Search, X } from 'lucide-react'
import {
  FocusEvent,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/shared/hooks/useDebounce'

/**
 * Base item type supported by {@link LookupConfirmField}.
 */
export interface LookupConfirmItem {
  /** Unique identifier for the suggestion option. */
  id: string | number
}

/**
 * Renderer contract for individual suggestions.
 */
export interface LookupSuggestionRenderOptions<TItem extends LookupConfirmItem> {
  /** Item being rendered. */
  item: TItem
  /** Whether the suggestion matches the currently selected item. */
  isSelected: boolean
  /** Whether the suggestion is currently highlighted via keyboard navigation. */
  isHighlighted: boolean
}

/**
 * Props for the {@link LookupConfirmField} component.
 */
export interface LookupConfirmFieldProps<TItem extends LookupConfirmItem> {
  /** Async loader returning validated results for the provided query. */
  fetchResults: (query: string) => Promise<TItem[]>
  /** Handler invoked when the confirm action is triggered with a selected item. */
  onConfirm: (item: TItem) => Promise<void> | void
  /** Optional handler invoked when the reject action is triggered. Shows X button when provided. */
  onReject?: (() => Promise<void> | void) | undefined
  /** Render prop for displaying suggestion content inside the list button. */
  renderSuggestion: (options: LookupSuggestionRenderOptions<TItem>) => ReactNode
  /** Extracts a user-friendly label for input reflection and accessibility. */
  getItemLabel?: (item: TItem) => string
  /** Placeholder text for the search input. */
  placeholder?: string
  /** Disables the field and suppresses network activity. */
  disabled?: boolean
  /** Minimum number of typed characters before querying kicks in. */
  minSearchLength?: number
  /** Maximum number of results rendered at once. */
  maxVisibleResults?: number
  /** Debounce interval (ms) before triggering search. */
  debounceMs?: number
  /** Accessible label for the confirm button. */
  confirmAriaLabel?: string
  /** Accessible label for the reject button. */
  rejectAriaLabel?: string
  /** React node displayed when no matches are present. */
  noResultsState?: ReactNode
  /** React node displayed during the loading state. */
  loadingState?: ReactNode
  /** Icon rendered inside the confirm button. */
  confirmIcon?: ReactNode
  /** Icon rendered inside the reject button. */
  rejectIcon?: ReactNode
  /** Optional custom query key base for TanStack Query caching. */
  queryKeyBase?: QueryKey
  /** Extra React Query configuration overrides. */
  queryOptions?: Omit<UseQueryOptions<TItem[], Error>, 'queryKey' | 'queryFn' | 'enabled'>
  /** Invoked when the selected suggestion changes. */
  onSelectionChange?: (item: TItem | null) => void
  /** Invoked whenever the raw query input mutates. */
  onQueryChange?: (value: string) => void
  /** Additional class name overrides for the root container. */
  className?: string
  /** Optional class name overrides for the underlying input. */
  inputClassName?: string
  /** Optional class name overrides for the input addon (e.g., search icon). */
  addonClassName?: string
  /** Icon rendered in the input addon (left side). Defaults to Search icon. */
  addonIcon?: ReactNode
  /** Optional header to render above suggestion list inside the popover. */
  renderHeader?: () => ReactNode
  /** Determines whether the field should clear itself when focus leaves the control. */
  clearOnBlur?: boolean
  /** Initial label to show as selected item (e.g., existing SKU name). Reverts to this value on blur. */
  initialLabel?: string | undefined
  /** Pre-selected item that enables immediate confirmation. Takes precedence over initialLabel. */
  initialSelectedItem?: TItem | undefined
  /** Optional callback invoked when user clicks "Create new" action. When provided, shows create action at top of dropdown. */
  onCreateNew?: () => void
  /** Label for the "Create new" action button. Defaults to "Create new". */
  createNewLabel?: string
}

/**
 * Inline lookup control with confirmation, reusable for SKU, transaction, or generic entity selection flows.
 *
 * @component
 * @example
 * ```tsx
 * <LookupConfirmField
 *   fetchResults={query => searchProducts(query)}
 *   onConfirm={item => assignProduct(item.id)}
 *   renderSuggestion={({ item }) => <span>{item.name}</span>}
 *   placeholder="Assign to existing SKU..."
 * />
 * ```
 */
export function LookupConfirmField<TItem extends LookupConfirmItem>({
  fetchResults,
  onConfirm,
  onReject,
  renderSuggestion,
  getItemLabel = item => String(item.id),
  placeholder = 'Search…',
  disabled = false,
  minSearchLength = 2,
  maxVisibleResults = 6,
  debounceMs = 300,
  confirmAriaLabel = 'Confirm selection',
  rejectAriaLabel = 'Reject',
  noResultsState = (
    <div className="py-4 text-center text-xs text-muted-foreground">No matches found.</div>
  ),
  loadingState = (
    <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
      <Loader2 className="size-4 animate-spin" />
      Searching…
    </div>
  ),
  confirmIcon = <Check className="size-4" />,
  rejectIcon = <X className="size-4" />,
  queryKeyBase = ['lookup-confirm-field'],
  queryOptions,
  onSelectionChange,
  onQueryChange,
  className,
  inputClassName,
  addonClassName,
  addonIcon = <Search className="size-4" />,
  renderHeader,
  clearOnBlur = true,
  initialLabel,
  initialSelectedItem,
  onCreateNew,
  createNewLabel = 'Create new',
}: LookupConfirmFieldProps<TItem>): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const closeTimerRef = useRef<number | null>(null)
  const prevInitialLabelRef = useRef<string | undefined>(initialLabel)
  const prevInitialSelectedItemRef = useRef<TItem | undefined>(initialSelectedItem)
  const listContainerRef = useRef<HTMLDivElement | null>(null)
  // If initialSelectedItem is provided, use its label; otherwise fall back to initialLabel
  const [query, setQuery] = useState<string>(
    initialSelectedItem ? getItemLabel(initialSelectedItem) : (initialLabel ?? '')
  )
  const [selectedItem, setSelectedItem] = useState<TItem | null>(initialSelectedItem ?? null)
  const [isInteracting, setIsInteracting] = useState<boolean>(false)
  const [isAnchorFocused, setIsAnchorFocused] = useState<boolean>(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)

  // Sync query with initialLabel only when it actually changes (e.g., after successful SKU update)
  // Don't update if user is actively editing
  useEffect(() => {
    if (initialLabel !== prevInitialLabelRef.current && !isInteracting && !isAnchorFocused) {
      setQuery(initialLabel ?? '')
      prevInitialLabelRef.current = initialLabel
    }
  }, [initialLabel, isInteracting, isAnchorFocused])

  // Sync with initialSelectedItem when it changes (e.g., AI suggestion)
  // This sets both the query text AND the selected item, enabling immediate confirmation
  useEffect(() => {
    if (initialSelectedItem !== prevInitialSelectedItemRef.current && !isInteracting && !isAnchorFocused) {
      if (initialSelectedItem) {
        setQuery(getItemLabel(initialSelectedItem))
        setSelectedItem(initialSelectedItem)
      } else {
        setSelectedItem(null)
      }
      prevInitialSelectedItemRef.current = initialSelectedItem
    }
  }, [initialSelectedItem, isInteracting, isAnchorFocused, getItemLabel])

  const debouncedQuery = useDebounce(query, debounceMs)
  const normalizedQuery = debouncedQuery.trim()
  const shouldQuery = normalizedQuery.length >= minSearchLength && !disabled
  const queryKey = useMemo(
    () => [...queryKeyBase, normalizedQuery] as QueryKey,
    [queryKeyBase, normalizedQuery]
  )

  const { data: fetchedResults = [], isFetching } = useQuery<TItem[], Error>({
    queryKey,
    queryFn: () => fetchResults(normalizedQuery),
    enabled: shouldQuery,
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  })

  const visibleResults = useMemo(
    () => shouldQuery ? fetchedResults.slice(0, maxVisibleResults) : [],
    [shouldQuery, fetchedResults, maxVisibleResults]
  )
  const hasCreateNew = Boolean(onCreateNew)
  const popoverOpen = (shouldQuery || hasCreateNew) && (isInteracting || isAnchorFocused)

  const clearCloseTimer = useCallback((): void => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const scheduleClose = useCallback((): void => {
    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(() => {
      setIsInteracting(false)
    }, 120)
  }, [clearCloseTimer])

  const dismissInteraction = useCallback((shouldClearSelection: boolean): void => {
    setIsInteracting(false)
    setIsAnchorFocused(false)
    if (shouldClearSelection) {
      // Revert to initial label instead of clearing completely
      setQuery(initialLabel ?? '')
      setSelectedItem(null)
    }
  }, [initialLabel])

  useEffect(() => {
    return () => {
      clearCloseTimer()
    }
  }, [clearCloseTimer])

  useEffect(() => {
    if (!clearOnBlur) return
    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target as HTMLElement | null
      const insideAnchor = target ? containerRef.current?.contains(target) === true : false
      const insidePopover = target?.closest('[data-slot="popover-content"]') !== null
      if (!insideAnchor && !insidePopover) {
        dismissInteraction(true)
      }
    }
    window.addEventListener('pointerdown', handlePointerDown)
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [clearOnBlur, dismissInteraction])

  useEffect(() => {
    onSelectionChange?.(selectedItem)
  }, [selectedItem, onSelectionChange])

  const handleSelect = useCallback(
    (item: TItem, index: number): void => {
      setSelectedItem(item)
      setQuery(getItemLabel(item))
      setIsInteracting(false)
      setHighlightedIndex(index)
    },
    [getItemLabel]
  )

  const handleConfirmClick = useCallback((): void => {
    if (!selectedItem || disabled) return
    setIsInteracting(false)
    setIsAnchorFocused(false)
    void onConfirm(selectedItem)
  }, [disabled, onConfirm, selectedItem])

  const handleRejectClick = useCallback((): void => {
    if (!onReject || disabled) return
    setIsInteracting(false)
    setIsAnchorFocused(false)
    void onReject()
  }, [disabled, onReject])

  const handleInputFocus = useCallback((): void => {
    clearCloseTimer()
    setIsAnchorFocused(true)
    setIsInteracting(true)
  }, [clearCloseTimer])

  const handleInputBlur = useCallback((): void => {
    scheduleClose()
    setIsAnchorFocused(false)
  }, [scheduleClose])

  const handleOpenChange = useCallback(
    (next: boolean): void => {
      // Only react to open requests; we ignore close requests here and
      // rely on our own blur/pointer handlers to prevent premature closing
      if (next && (shouldQuery || hasCreateNew)) {
        setIsInteracting(true)
      }
    },
    [shouldQuery, hasCreateNew]
  )

  const handleFocusLeave = useCallback(
    (event: FocusEvent<HTMLDivElement>): void => {
      if (!clearOnBlur) return
      const nextTarget = event.relatedTarget as HTMLElement | null
      const insideAnchor = nextTarget ? event.currentTarget.contains(nextTarget) : false
      const insidePopover = nextTarget?.closest('[data-slot="popover-content"]') !== null
      if (!insideAnchor && !insidePopover) {
        dismissInteraction(true)
      }
    },
    [clearOnBlur, dismissInteraction]
  )

  const inputPlaceholder = placeholder
  const confirmDisabled = disabled || !selectedItem
  const highlightedItem = highlightedIndex >= 0 ? (visibleResults[highlightedIndex] ?? null) : null

  useEffect(() => {
    if (!shouldQuery) {
      setHighlightedIndex(-1)
      return
    }
    // When results change, reset highlight appropriately
    setHighlightedIndex(previous => {
      // If no results, highlight create action if available
      if (visibleResults.length === 0) {
        return onCreateNew ? -1 : -1
      }
      // If we were on create action, stay there
      if (previous === -1 && onCreateNew) {
        return -1
      }
      // Otherwise, ensure we're within bounds or go to first result
      if (previous < 0 || previous >= visibleResults.length) {
        return 0
      }
      return previous
    })
  }, [normalizedQuery, shouldQuery, visibleResults.length, onCreateNew])

  useEffect(() => {
    if (!selectedItem || !visibleResults.length) return
    const matchIndex = visibleResults.findIndex(item => item.id === selectedItem.id)
    if (matchIndex >= 0) {
      setHighlightedIndex(matchIndex)
    }
  }, [selectedItem, visibleResults])

  useEffect(() => {
    if (highlightedIndex < 0) return
    const container = listContainerRef.current
    if (!container) return
    const highlighted = container.querySelector<HTMLButtonElement>('[data-highlighted="true"]')
    highlighted?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex])

  // Use CREATE_NEW_INDEX to represent the "Create new" action in navigation
  const CREATE_NEW_INDEX = -1

  const handleArrowNavigation = useCallback(
    (direction: 'up' | 'down'): void => {
      const hasResults = visibleResults.length > 0
      if (!hasResults && !hasCreateNew) return

      setHighlightedIndex(prev => {
        if (direction === 'down') {
          // Going down: create -> 0 -> 1 -> ... -> last -> create
          if (hasCreateNew && prev === CREATE_NEW_INDEX) {
            return hasResults ? 0 : CREATE_NEW_INDEX
          }
          if (hasResults && prev >= visibleResults.length - 1) {
            return hasCreateNew ? CREATE_NEW_INDEX : 0
          }
          return hasResults ? prev + 1 : CREATE_NEW_INDEX
        }
        // Going up: create <- 0 <- 1 <- ... <- last <- create
        if (hasCreateNew && prev === CREATE_NEW_INDEX) {
          return hasResults ? visibleResults.length - 1 : CREATE_NEW_INDEX
        }
        if (prev <= 0) {
          return hasCreateNew ? CREATE_NEW_INDEX : (hasResults ? visibleResults.length - 1 : CREATE_NEW_INDEX)
        }
        return prev - 1
      })
      setIsInteracting(true)
      setIsAnchorFocused(true)
    },
    [visibleResults.length, hasCreateNew, CREATE_NEW_INDEX]
  )

  const handleCreateNewClick = useCallback((): void => {
    if (!onCreateNew) return
    setIsInteracting(false)
    setIsAnchorFocused(false)
    onCreateNew()
  }, [onCreateNew])

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      const hasResults = visibleResults.length > 0
      if (!hasResults && !hasCreateNew) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        handleArrowNavigation('down')
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        handleArrowNavigation('up')
      } else if (event.key === 'Enter') {
        // Handle "Create new" action
        if (highlightedIndex === CREATE_NEW_INDEX && onCreateNew) {
          event.preventDefault()
          handleCreateNewClick()
          return
        }
        if (highlightedItem) {
          event.preventDefault()
          if (selectedItem && highlightedItem.id === selectedItem.id) {
            handleConfirmClick()
            return
          }
          const index = visibleResults.findIndex(item => item.id === highlightedItem.id)
          if (index >= 0) {
            handleSelect(highlightedItem, index)
          }
        } else if (selectedItem) {
          event.preventDefault()
          handleConfirmClick()
        }
      }
    },
    [
      handleArrowNavigation,
      handleSelect,
      handleConfirmClick,
      handleCreateNewClick,
      highlightedIndex,
      highlightedItem,
      selectedItem,
      visibleResults,
      hasCreateNew,
      onCreateNew,
      CREATE_NEW_INDEX,
    ]
  )

  const isCreateNewHighlighted = highlightedIndex === CREATE_NEW_INDEX

  const renderCreateNewAction = (): ReactNode => {
    if (!onCreateNew) return null
    return (
      <button
        type="button"
        onMouseDown={clearCloseTimer}
        onClick={handleCreateNewClick}
        onMouseEnter={() => setHighlightedIndex(CREATE_NEW_INDEX)}
        data-highlighted={isCreateNewHighlighted ? 'true' : undefined}
        className={cn(
          'flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium transition',
          isCreateNewHighlighted
            ? 'bg-secondary text-foreground'
            : 'text-foreground hover:bg-secondary/50'
        )}
      >
        <Plus className="size-4" />
        {createNewLabel}
      </button>
    )
  }

  const renderListContent = (): ReactNode => {
    if (isFetching) {
      return (
        <div>
          {renderHeader ? (
            <div className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground/80">
              {renderHeader()}
            </div>
          ) : null}
          {renderCreateNewAction()}
          {onCreateNew && <div className="border-t border-border/15" />}
          {loadingState}
        </div>
      )
    }
    if (visibleResults.length === 0) {
      return (
        <div>
          {renderHeader ? (
            <div className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground/80">
              {renderHeader()}
            </div>
          ) : null}
          {renderCreateNewAction()}
          {normalizedQuery.length > 0 && onCreateNew && <div className="border-t border-border/15" />}
          {normalizedQuery.length > 0 && noResultsState}
        </div>
      )
    }
    return (
      <div>
        {renderHeader ? (
          <div className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground/80">
            {renderHeader()}
          </div>
        ) : null}
        {renderCreateNewAction()}
        {onCreateNew && <div className="border-t border-border/15" />}
        <div
          ref={listContainerRef}
          className="max-h-80 overflow-y-auto pr-1 [scrollbar-width:thin] [scrollbar-color:var(--color-border)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border/40"
        >
          <ul role="listbox" aria-label="Search suggestions" className="divide-y divide-border/15">
            {visibleResults.map((item, index) => {
              const isSelected = selectedItem?.id === item.id
              const isHighlighted = highlightedIndex === index
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onMouseDown={clearCloseTimer}
                    onClick={() => handleSelect(item, index)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    data-highlighted={isHighlighted ? 'true' : undefined}
                    className={cn(
                      'w-full px-4 py-2 text-left text-sm transition',
                      isHighlighted || isSelected
                        ? 'bg-secondary text-foreground'
                        : 'text-foreground hover:bg-secondary/50'
                    )}
                    aria-label={getItemLabel(item)}
                  >
                    {renderSuggestion({ item, isSelected, isHighlighted })}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn('w-full max-w-[260px] space-y-2', className)}
      onBlurCapture={handleFocusLeave}
    >
      <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div className="w-full">
            <InputGroup>
              <InputGroupAddon aria-label="Search catalog" className={addonClassName}>
                {addonIcon}
              </InputGroupAddon>
              <InputGroupInput
                value={query}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                onChange={event => {
                  const nextValue = event.target.value
                  setQuery(nextValue)
                  setSelectedItem(null)
                  if (nextValue.trim().length === 0) {
                    setHighlightedIndex(-1)
                  }
                  onQueryChange?.(nextValue)
                  if (nextValue.trim().length >= minSearchLength) {
                    setIsInteracting(true)
                    setIsAnchorFocused(true)
                  } else if (nextValue.trim().length === 0 && clearOnBlur) {
                    dismissInteraction(false)
                  } else {
                    setIsInteracting(false)
                  }
                }}
                placeholder={inputPlaceholder}
                aria-label="Lookup input"
                disabled={disabled}
                className={inputClassName}
              />
              {onReject && (
                <InputGroupButton
                  aria-label={rejectAriaLabel}
                  onClick={handleRejectClick}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  disabled={disabled}
                  size="icon-sm"
                  variant="ghost"
                  className="text-muted-foreground hover:text-destructive"
                >
                  {rejectIcon}
                </InputGroupButton>
              )}
              <InputGroupButton
                aria-label={confirmAriaLabel}
                onClick={handleConfirmClick}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                disabled={confirmDisabled}
                size="icon-sm"
                variant="secondary"
                className={cn(
                  'transition-all',
                  confirmDisabled
                    ? 'pointer-events-none opacity-0'
                    : 'opacity-100 hover:bg-primary hover:text-primary-foreground'
                )}
              >
                {confirmIcon}
              </InputGroupButton>
            </InputGroup>
          </div>
        </PopoverAnchor>
        {shouldQuery ? (
          <PopoverContent
            align="start"
            side="bottom"
            sideOffset={12}
            className="w-[130%] min-w-[260px] border border-border/20 bg-[color:var(--glass-surface-light)] p-0 shadow-xl"
            style={{ backgroundColor: 'var(--glass-surface-light)' }}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
            onOpenAutoFocus={event => event.preventDefault()}
            onCloseAutoFocus={event => event.preventDefault()}
          >
            {renderListContent()}
          </PopoverContent>
        ) : null}
      </Popover>
    </div>
  )
}
